import { CdkDrag, CdkDragDrop, CdkDropList, CdkDropListGroup, DragAxis, moveItemInArray, transferArrayItem } from "@angular/cdk/drag-drop";
import { Component, Input, OnDestroy } from "@angular/core";
import { LayoutAlignDirective, LayoutDirective } from "@ngbracket/ngx-layout/flex";
import { TruncateTextComponent } from "@tylertech/common-components-module";
import { ForgeIconModule } from "@tylertech/forge-angular";

@Component({
    selector: "drag-and-drop-list",
    templateUrl: "./drag-and-drop-list.tmpl.html",
    styleUrls: ["./drag-and-drop-list.component.scss"],
    imports: [
        CdkDrag,
        CdkDropList,
        CdkDropListGroup,
        LayoutAlignDirective,
        LayoutDirective,
        ForgeIconModule,
        TruncateTextComponent,
    ]
})
export class DragAndDropListComponent<T extends object = Record<string, unknown>> implements OnDestroy
{
    @Input() readonly groups: Array<Array<T>>;
    @Input() readonly groupTitles: Array<string>;
    @Input() readonly display: keyof T & string;
    @Input() readonly subtitleMember: keyof T & string;

    screenReaderAnnouncement: string = "";

    private keyboardMoveMode: boolean = false;
    private movingGroupIndex: number | null = null;
    private movingItemIndex: number | null = null;
    private originalGroupIndex: number | null = null;
    private originalItemIndex: number | null = null;
    private announceTimeoutId: ReturnType<typeof setTimeout> | null = null;

    getDisplay(item: T): string
    {
        return item[this.display]?.toString();
    }

    getSubtitle(item: T): string
    {
        return item[this.subtitleMember]?.toString();
    }

    onCdkDropListDropped(event: CdkDragDrop<Array<T>>): void
    {
        if (event.previousContainer === event.container)
        {
            moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
        }
        else
        {
            transferArrayItem(
                event.previousContainer.data,
                event.container.data,
                event.previousIndex,
                event.currentIndex
            );
        }
    }

    get isKeyboardMoveMode(): boolean
    {
        return this.keyboardMoveMode;
    }

    isMoving(groupIndex: number, itemIndex: number): boolean
    {
        return this.keyboardMoveMode
            && this.movingGroupIndex === groupIndex
            && this.movingItemIndex === itemIndex;
    }

    getItemAriaLabel(item: T, groupIndex: number, itemIndex: number): string
    {
        const subtitle = this.getSubtitle(item);
        const subtitleClause = subtitle ? `, ${subtitle}` : "";
        return `${this.getDisplay(item)}${subtitleClause}, item ${itemIndex + 1} of ${this.groups[groupIndex].length}. Press Space to move.`;
    }

    onItemKeyDown(event: KeyboardEvent, groupIndex: number, itemIndex: number): void
    {
        if (event.key === " " || event.key === "Spacebar" || event.key === "Enter")
        {
            event.preventDefault();
            if (!this.keyboardMoveMode)
            {
                this.enterKeyboardMoveMode(groupIndex, itemIndex);
            }
            else
            {
                this.commitMove();
            }
            return;
        }

        if (!this.keyboardMoveMode || this.movingGroupIndex !== groupIndex)
        {
            return;
        }

        if (event.key === "ArrowUp")
        {
            event.preventDefault();
            this.moveItem(-1);
        }
        else if (event.key === "ArrowDown")
        {
            event.preventDefault();
            this.moveItem(1);
        }
        else if (event.key === "ArrowLeft")
        {
            event.preventDefault();
            this.moveToGroup(-1);
        }
        else if (event.key === "ArrowRight")
        {
            event.preventDefault();
            this.moveToGroup(1);
        }
        else if (event.key === "Escape")
        {
            event.preventDefault();
            event.stopPropagation();
            this.cancelMove();
        }
    }

    onItemBlur(event: FocusEvent): void
    {
        if (!this.keyboardMoveMode)
        {
            return;
        }

        // Reordering keeps the same DOM node (tracked by item identity), so a
        // reorder does not blur the item. A blur with a concrete new target
        // therefore means focus genuinely left the item, so commit the move.
        // Ignore blurs with no related target (transient focus churn).
        const related = event.relatedTarget as HTMLElement | null;
        if (!related)
        {
            return;
        }

        this.commitMove();
    }

    ngOnDestroy(): void
    {
        if (this.announceTimeoutId !== null)
        {
            clearTimeout(this.announceTimeoutId);
        }
    }

    private enterKeyboardMoveMode(groupIndex: number, itemIndex: number): void
    {
        this.keyboardMoveMode = true;
        this.movingGroupIndex = groupIndex;
        this.movingItemIndex = itemIndex;
        this.originalGroupIndex = groupIndex;
        this.originalItemIndex = itemIndex;

        const item = this.groups[groupIndex][itemIndex];
        const crossGroupClause = this.groups.length > 1
            ? " left and right arrow keys to move between groups,"
            : "";
        this.announceToScreenReader(
            `Move mode activated for ${this.getDisplay(item)}, position ${itemIndex + 1} of ${this.groups[groupIndex].length}. `
            + `Use the up and down arrow keys to move,${crossGroupClause} Space or Enter to drop, Escape to cancel.`
        );
    }

    private moveItem(direction: number): void
    {
        if (this.movingGroupIndex === null || this.movingItemIndex === null)
        {
            return;
        }

        const list = this.groups[this.movingGroupIndex];
        const from = this.movingItemIndex;
        const to = from + direction;

        if (to < 0 || to >= list.length)
        {
            const boundary = direction < 0 ? "top" : "bottom";
            this.announceToScreenReader(`${this.getDisplay(list[from])} is already at the ${boundary} of the list.`);
            return;
        }

        moveItemInArray(list, from, to);
        this.movingItemIndex = to;

        const item = list[to];
        this.announceToScreenReader(`${this.getDisplay(item)}, position ${to + 1} of ${list.length}.`);
        this.focusMovingItemAfterRender(this.movingGroupIndex, to);
    }

    private moveToGroup(direction: number): void
    {
        if (this.movingGroupIndex === null || this.movingItemIndex === null)
        {
            return;
        }

        if (this.groups.length < 2)
        {
            return;
        }

        const targetGroupIndex = this.movingGroupIndex + direction;
        if (targetGroupIndex < 0 || targetGroupIndex >= this.groups.length)
        {
            const boundary = direction < 0 ? "first" : "last";
            const item = this.groups[this.movingGroupIndex][this.movingItemIndex];
            this.announceToScreenReader(`${this.getDisplay(item)} is already in the ${boundary} group.`);
            return;
        }

        const sourceList = this.groups[this.movingGroupIndex];
        const targetList = this.groups[targetGroupIndex];

        // Insert at the same position when possible, otherwise append.
        const targetItemIndex = Math.min(this.movingItemIndex, targetList.length);
        transferArrayItem(sourceList, targetList, this.movingItemIndex, targetItemIndex);

        this.movingGroupIndex = targetGroupIndex;
        this.movingItemIndex = targetItemIndex;

        const item = targetList[targetItemIndex];
        const groupName = this.getGroupName(targetGroupIndex);
        this.announceToScreenReader(
            `${this.getDisplay(item)} moved to ${groupName}, position ${targetItemIndex + 1} of ${targetList.length}.`
        );
        this.focusMovingItemAfterRender(targetGroupIndex, targetItemIndex);
    }

    private getGroupName(groupIndex: number): string
    {
        return this.groupTitles?.[groupIndex] ?? `group ${groupIndex + 1}`;
    }

    private commitMove(): void
    {
        if (this.movingGroupIndex === null || this.movingItemIndex === null)
        {
            this.exitKeyboardMoveMode();
            return;
        }

        const list = this.groups[this.movingGroupIndex];
        const item = list[this.movingItemIndex];
        const groupClause = this.groups.length > 1
            ? ` in ${this.getGroupName(this.movingGroupIndex)}`
            : "";
        this.announceToScreenReader(`${this.getDisplay(item)} dropped at position ${this.movingItemIndex + 1} of ${list.length}${groupClause}.`);
        this.exitKeyboardMoveMode();
    }

    private cancelMove(): void
    {
        if (this.movingGroupIndex === null || this.movingItemIndex === null
            || this.originalGroupIndex === null || this.originalItemIndex === null)
        {
            this.exitKeyboardMoveMode();
            return;
        }

        const originalGroupIndex = this.originalGroupIndex;
        const originalIndex = this.originalItemIndex;
        const originalList = this.groups[originalGroupIndex];

        // The item may have been moved to a different group during this session,
        // so return it to its original group and position.
        if (this.movingGroupIndex !== originalGroupIndex)
        {
            transferArrayItem(
                this.groups[this.movingGroupIndex],
                originalList,
                this.movingItemIndex,
                originalIndex
            );
        }
        else
        {
            moveItemInArray(originalList, this.movingItemIndex, originalIndex);
        }

        this.announceToScreenReader(`Move cancelled. ${this.getDisplay(originalList[originalIndex])} returned to its original position.`);
        this.focusMovingItemAfterRender(originalGroupIndex, originalIndex);
        this.exitKeyboardMoveMode();
    }

    private exitKeyboardMoveMode(): void
    {
        this.keyboardMoveMode = false;
        this.movingGroupIndex = null;
        this.movingItemIndex = null;
        this.originalGroupIndex = null;
        this.originalItemIndex = null;
    }

    private focusMovingItemAfterRender(groupIndex: number, itemIndex: number): void
    {
        setTimeout(() =>
        {
            const element = document.querySelector(
                `forge-list-item[data-group-index="${groupIndex}"][data-item-index="${itemIndex}"]`
            ) as HTMLElement | null;
            element?.focus();
        }, 0);
    }

    private announceToScreenReader(message: string): void
    {
        this.screenReaderAnnouncement = message;

        if (this.announceTimeoutId !== null)
        {
            clearTimeout(this.announceTimeoutId);
        }

        this.announceTimeoutId = setTimeout(() =>
        {
            this.screenReaderAnnouncement = "";
            this.announceTimeoutId = null;
        }, 1000);
    }

    get lockVerticalDrag(): DragAxis | null
    {
        return this.groups.length === 1 ? "y" : null;
    }
}
